import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { RefreshCw } from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../App";

// Icon components
const LocationIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
      clipRule="evenodd"
    />
  </svg>
);

const SearchIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z"
      clipRule="evenodd"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
      clipRule="evenodd"
    />
  </svg>
);

const TodayIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
      clipRule="evenodd"
    />
  </svg>
);

const GlobeIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
      clipRule="evenodd"
    />
  </svg>
);

const BusinessIcon = () => (
  <svg
    className="w-12 h-12"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const ChevronIcon = ({ direction = "right" }) => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
    {direction === "right" ? (
      <path
        fillRule="evenodd"
        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
        clipRule="evenodd"
      />
    ) : (
      <path
        fillRule="evenodd"
        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    )}
  </svg>
);

function BossPlaces() {
  const history = useHistory();
  const [selectedPlaceType, setSelectedPlaceType] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [allData, setAllData] = useState({
    myPlaces: [],
    myDailyWork: [],
    myMonthlySalary: [],
    myEmployees: [],
    myExpenses: [],
  });
  const [choosedPlace, setChoosedPlace] = useState(null);

  // Get language from localStorage or default to 'en'
  const Lang = localStorage.getItem("language") || "en";
  const isRTL = Lang === "ar" || Lang === "he";

  const currentMonth = moment().format("MM");
  const currentYear = moment().format("YYYY");

  // Get user data from localStorage on mount
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      setUserData(JSON.parse(user));
    }
  }, []);

  // Fetch data when userData is available
  const fetchData = async () => {
    if (!userData) return;

    setLoading(true);
    try {
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: userData.id,
      });
      console.log("📊 Fetched data:", response.data);
      setAllData(
        response.data || {
          myPlaces: [],
          myDailyWork: [],
          myMonthlySalary: [],
          myEmployees: [],
          myExpenses: [],
        },
      );
    } catch (err) {
      console.error("❌ Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchData();
    }
  }, [userData]);

  const places = allData.myPlaces || [];

  const filteredPlaces = places
    .filter((place) =>
      selectedPlaceType ? place.placeType === selectedPlaceType : true,
    )
    .filter((place) =>
      searchQuery.trim()
        ? place.place.toLowerCase().includes(searchQuery.toLowerCase())
        : true,
    );

  const handlePlacePress = (place) => {
    setChoosedPlace(place);
    // You might want to save this to localStorage or context
    localStorage.setItem("choosedPlace", JSON.stringify(place));

    if (place.placeType === 2) {
      history.push("/boss-statistic-temporary-place-data");
    } else {
      history.push("/boss-statistic-places-data");
    }
  };

  const getPlaceStats = (place) => {
    const placeId = place.id;

    const placeWork = (allData.myDailyWork || []).filter((work) => {
      const [day, month, year] = work.date.split("-");
      return (
        work.placeId === placeId &&
        month === currentMonth &&
        year === currentYear
      );
    });

    const totalDailySalaries = placeWork.reduce(
      (sum, work) => sum + (parseFloat(work.salary) || 0),
      0,
    );

    const placeMonthlySalaries = Array.isArray(allData?.myMonthlySalary)
      ? allData.myMonthlySalary.filter((item) => {
          const [day, month, year] = item.date.split("-");
          return (
            item.placeId === placeId &&
            month === currentMonth &&
            year === currentYear
          );
        })
      : [];

    const totalMonthlySalaries = placeMonthlySalaries.reduce((sum, item) => {
      const employee = (allData.myEmployees || []).find(
        (e) => e.id === item.employeeId,
      );
      return sum + (employee?.monthlySalary || 0);
    }, 0);

    const totalSalaries = totalDailySalaries + totalMonthlySalaries;
    const workDays = new Set(placeWork.map((work) => work.date)).size;

    const placeExpenses = (allData.myExpenses || []).filter((expense) => {
      const [day, month, year] = expense.date.split("-");
      return (
        expense.placeId === placeId &&
        month === currentMonth &&
        year === currentYear
      );
    });

    const totalExpenses = placeExpenses.reduce(
      (sum, expense) => sum + (parseFloat(expense.price) || 0),
      0,
    );

    let revenue = 0;

    if (place.paidWay === 2) {
      revenue = placeWork.reduce((sum, work) => {
        return sum + (parseFloat(work.placeDailySalary) || 0);
      }, 0);
    } else if (place.paidWay === 3) {
      revenue = parseFloat(place.profit || 0);
    } else {
      revenue = parseFloat(place.profit || 0);
    }

    const netProfit = revenue - totalSalaries - totalExpenses;

    return { revenue, totalSalaries, totalExpenses, workDays, netProfit };
  };

  const getPaymentTypeLabel = (paidWay) => {
    if (paidWay === 1) return "Monthly";
    if (paidWay === 2) return "Daily";
    if (paidWay === 3) return "Global";
    return "";
  };

  const renderPlaceCard = (item) => {
    const { revenue, totalSalaries, totalExpenses, workDays, netProfit } =
      getPlaceStats(item);

    return (
      <div
        key={item.id}
        onClick={() => handlePlacePress(item)}
        className="bg-white rounded-2xl p-3 cursor-pointer shadow-sm hover:shadow-md transition-shadow border border-gray-100 relative w-full"
      >
        <div className={`flex items-center ${isRTL ? "flex-row-reverse" : ""}`}>
          <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-white mx-2">
            <LocationIcon />
          </div>
          <div className="flex-1">
            <h3
              className={`text-sm font-bold text-gray-900 mb-0.5 truncate ${
                isRTL ? "text-right" : "text-left"
              }`}
            >
              {item.place}
            </h3>
            <div
              className={`flex items-center gap-1.5 ${isRTL ? "flex-row-reverse" : ""}`}
            >
              <span
                className={`text-xs text-gray-400 font-medium ${isRTL ? "text-right" : "text-left"}`}
              >
                ID: {item.id}
              </span>
              {item.paidWay && (
                <div className="flex items-center gap-1 bg-gray-100 px-1 py-0.5 rounded">
                  <span className="text-gray-600">
                    {item.paidWay === 1 ? (
                      <CalendarIcon />
                    ) : item.paidWay === 2 ? (
                      <TodayIcon />
                    ) : (
                      <GlobeIcon />
                    )}
                  </span>
                  <span
                    className={`text-xs font-semibold text-gray-600 ${isRTL ? "text-right" : "text-left"}`}
                  >
                    {getPaymentTypeLabel(item.paidWay)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className={`absolute top-3 ${isRTL ? "left-3" : "right-3"} w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center`}
        >
          <span className="text-gray-300">
            <ChevronIcon direction={isRTL ? "left" : "right"} />
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50 h-full">
      {/* Search Bar */}
      <div className="mx-4 mt-4 mb-2">
        <div className="flex items-center bg-white px-3 py-2.5 rounded-xl shadow-sm">
          <span className={`text-gray-400 ${isRTL ? "ml-2" : "mr-2"}`}>
            <SearchIcon />
          </span>
          <input
            type="text"
            className={`flex-1 text-sm text-gray-900 outline-none ${
              isRTL ? "text-right" : "text-left"
            }`}
            placeholder="Search by place name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            dir={isRTL ? "rtl" : "ltr"}
          />
          {searchQuery.length > 0 && (
            <button
              onClick={() => setSearchQuery("")}
              className={`p-1 text-gray-400 hover:text-gray-600 ${
                isRTL ? "mr-1" : "ml-1"
              }`}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Refresh Button */}
      <div className="mx-4 mb-4">
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Places Grid or Empty State */}
      {filteredPlaces.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-10">
          <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-5 text-gray-300">
            <BusinessIcon />
          </div>
          <h3
            className={`text-lg font-semibold text-gray-600 mb-2 text-center ${isRTL ? "text-right" : "text-left"}`}
          >
            {searchQuery ? "No places match your search" : "No places yet"}
          </h3>
          <p
            className={`text-sm text-gray-400 text-center leading-5 ${isRTL ? "text-right" : "text-left"}`}
          >
            {searchQuery
              ? "Try a different search term"
              : "Add places to track your work"}
          </p>
        </div>
      ) : (
        <div className="p-4 pt-3 pb-24 grid grid-cols-2 gap-3 overflow-y-auto max-h-[calc(100vh-250px)]">
          {filteredPlaces.map((place) => renderPlaceCard(place))}
        </div>
      )}
    </div>
  );
}

export default BossPlaces;
